/* selv-collection.js · Selv v6, lot COLLECTIONS · vanilla, sans bibliothèque, chargé en defer.
   Amélioration progressive : sans ce fichier, les pilules sont des liens, la feuille « Filtrer et trier » reste
   celle de Flow (component-filtering.js) et l'introduction s'affiche en entier.
   1. Bouton flottant en verre : visible une fois la barre d'outils dépassée, masqué dès la fin de la grille (FAQ, pied
      de page), même après un saut de défilement.
   2. Boutons « Filtrer et trier » : ouvrent la feuille de Flow, rendent le focus au bouton d'origine à la fermeture.
   3. Feuille : sémantique de dialogue, groupes de filtres ouverts, glisser vers le bas pour fermer, « Voir les N produits ».
   4. Pilules de filtre : filtrage sur place par l'API de Flow (eventBus « collection:filter:apply »).
   5. Épuisés toujours en fin de grille, même après « Afficher plus » ; seules les cartes vraiment nouvelles
      apparaissent en fondu ; le focus passe au 1er produit ajouté, annoncé aux lecteurs d'écran.
   6. Introduction repliée : « Lire la suite » / « Réduire » (bouton masqué si tout le texte tient).
   7. Bandeau de campagne : état « Code appliqué » quand le témoin discount_code (posé par /discount/CODE) porte le code,
      même panier vide (cart.discount_codes reste vide tant que le panier l'est). Bandeau discret (la barre d'annonce
      porte la campagne) : il n'apparaît que dans cet état ; un script en ligne l'a déjà montré avant l'affichage, ceci
      n'est qu'un repli. Les cartes passent au prix réduit (classe html.lq-deal-on, posée elle aussi en ligne).
   8. Recherche : « Afficher plus » ajoute la page suivante sur place (Section Rendering API), sans recharger ; sans JS
      ou en cas d'échec, le lien mène à la page suivante.
   9. Fourchettes de prix (lot FILTRES ET RECHERCHE, 18 oct. 2026) : la pilule remplit « De » et « À », puis Flow filtre ;
      2e geste sur la pilule enfoncée = « Réinitialiser » du prix. Sans JS, les champs restent utilisables.
  10. Focus gardé après un filtrage (même lot) : Flow remplace le contenu des [data-filter-update] et le focus tombait sur
      <body> ; la case, l'interrupteur, la pilule ou le lien touché est retrouvé dans le nouveau contenu.
   Performance : aucune lecture de mise en page forcée à l'initialisation (pilule active recentrée par un
   IntersectionObserver, cartes déjà vues gardées dans un WeakSet plutôt qu'en attributs) : le 1er recalcul de styles
   de la page n'est jamais payé par ce script. */
(() => {
  'use strict';
  if (window.__selvCollection) return; // la feuille peut être liée par l'en-tête et par la grille
  window.__selvCollection = true;
  const d = document;
  const body = d.body;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const OPEN_CLASS = 'js-drawer-open-filter';

  /* 6. Introduction repliée ------------------------------------------------------------- */
  d.querySelectorAll('[data-lq-more]').forEach((btn) => {
    const target = d.getElementById(btn.getAttribute('aria-controls'));
    if (!target) return;
    const wrap = btn.closest('.lq-chead__introwrap');
    // Le bouton est posé sur la dernière ligne (position absolue) : le masquer ne décale rien.
    const sync = () => {
      if (target.classList.contains('is-open')) { btn.hidden = false; return; }
      btn.hidden = target.scrollHeight <= target.clientHeight + 2;
    };
    btn.addEventListener('click', () => {
      const open = !target.classList.contains('is-open');
      target.classList.toggle('is-open', open);
      if (wrap) wrap.classList.toggle('is-folded', !open);
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? btn.dataset.less : btn.dataset.more;
      if (!open) {
        const top = target.getBoundingClientRect().top;
        if (top < 0) target.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    });
    if ('ResizeObserver' in window) new ResizeObserver(sync).observe(target);
    else sync();
  });

  /* 7. Bandeau de campagne : code déjà appliqué par le lien /discount/CODE --------------- */
  const cookieCode = (() => {
    const m = d.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    if (!m) return '';
    try { return decodeURIComponent(m[1]).trim().toUpperCase(); } catch (e) { return ''; }
  })();
  d.querySelectorAll('[data-lq-promo]').forEach((promo) => {
    const code = (promo.getAttribute('data-lq-code') || '').toUpperCase();
    if (!code || cookieCode.split(',').map((c) => c.trim()).indexOf(code) === -1) return;
    const ends = Number(promo.getAttribute('data-lq-ends')) || 0;
    if (ends && ends * 1000 <= Date.now()) return;
    const apply = promo.querySelector('[data-lq-promo-apply]');
    const done = promo.querySelector('[data-lq-promo-done]');
    if (!done) return;
    if (apply) apply.hidden = true;
    done.hidden = false;
    promo.hidden = false;
    d.documentElement.classList.add('lq-deal-on');
  });

  const colls = d.querySelectorAll('[data-lq-coll]');
  if (!colls.length) return;

  colls.forEach((coll) => {
    const panel = coll.querySelector('collection-filtering-form');
    const fab = coll.querySelector('[data-lq-fab]');
    const flowOpen = coll.querySelector('[data-drawer-open-btn]');
    let lastOpener = null;
    let wasOpen = false;

    /* 2. Ouvrir la feuille depuis n'importe quel bouton ----------------------------------- */
    const openSheet = (opener) => {
      lastOpener = opener || flowOpen;
      const go = () => { if (flowOpen) flowOpen.click(); };
      if (customElements.get('collection-filtering-form')) go();
      else customElements.whenDefined('collection-filtering-form').then(go);
    };
    coll.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-lq-filter-open]');
      if (opener) { e.preventDefault(); openSheet(opener); return; }
      if (fab && e.target.closest('[data-lq-fab]')) lastOpener = fab;
    });

    /* 3. Feuille : état ouvert, sémantique, focus rendu ----------------------------------- */
    const setExpanded = (open) => {
      coll.querySelectorAll('[data-lq-filter-open], [data-lq-fab]').forEach((b) => b.setAttribute('aria-expanded', String(open)));
    };
    const onOpen = () => {
      if (!panel) return;
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      // Après le clic d'ouverture : Flow referme les <details> au clic sur le document.
      setTimeout(() => {
        const groups = panel.querySelectorAll('.facets__form-inner.mobile details');
        if (groups.length <= 3) groups.forEach((g) => g.setAttribute('open', ''));
        else if (!panel.querySelector('.facets__form-inner.mobile details[open]')) groups[0].setAttribute('open', '');
      }, 60);
      setExpanded(true);
    };
    const onClose = () => {
      setExpanded(false);
      if (panel) { panel.style.removeProperty('--lq-drag'); panel.classList.remove('is-dragging'); }
      const shown = (el) => el && el.isConnected && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
      const back = shown(lastOpener) ? lastOpener : coll.querySelector('[data-lq-filter-open]');
      if (back) setTimeout(() => back.focus({ preventScroll: true }), 0);
    };
    new MutationObserver(() => {
      const open = body.classList.contains(OPEN_CLASS);
      if (open === wasOpen) return;
      wasOpen = open;
      if (open) onOpen(); else onClose();
    }).observe(body, { attributes: true, attributeFilter: ['class'] });

    /* 9. Fourchettes de prix ---------------------------------------------------------------- */
    // component-filtering.js (Flow) lit une borne sur l'élément qui émet « input » et l'autre dans le formulaire, puis
    // recopie la valeur dans les deux copies (bureau, téléphone) : on remplit les deux copies, puis « input » part du champ
    // « À » (ou « De » quand la fourchette n'a pas de plafond). Retour immédiat : pilule enfoncée et grille en chargement,
    // Flow attendant 800 ms avant de filtrer.
    const applyPreset = (btn) => {
      const form = btn.closest('form');
      if (!form) return;
      if (btn.getAttribute('aria-pressed') === 'true') {
        const reset = form.querySelector('.price-filter-reset');
        if (reset) reset.click();
        return;
      }
      const min = btn.getAttribute('data-lq-price-min') || '';
      const max = btn.getAttribute('data-lq-price-max') || '';
      const mins = form.querySelectorAll('input[data-price-range-min]');
      const maxs = form.querySelectorAll('input[data-price-range-max]');
      const src = max ? maxs[0] : mins[0];
      if (!src) return;
      mins.forEach((i) => { i.value = min; });
      maxs.forEach((i) => { i.value = max; });
      form.querySelectorAll('[data-lq-price-min]').forEach((b) => {
        const on = b.getAttribute('data-lq-price-min') === min && b.getAttribute('data-lq-price-max') === max;
        b.setAttribute('aria-pressed', String(on));
      });
      const grid = d.querySelector('#CollectionProductGrid > div');
      if (grid) grid.classList.add('loading');
      src.dispatchEvent(new Event('input', { bubbles: true }));
    };

    if (panel) {
      // Les clics dans la feuille ne remontent pas au document : Flow y referme tous les <details> au moindre clic.
      panel.addEventListener('click', (e) => {
        const preset = e.target.closest('[data-lq-price-min]');
        if (preset) applyPreset(preset);
        const done = e.target.closest('[data-lq-sheet-done]');
        if (done) {
          const close = panel.querySelector('[data-drawer-close-btn]');
          if (close) close.click();
        }
        e.stopPropagation();
      });

      // Glisser la poignée vers le bas pour fermer (téléphone)
      const grab = panel.querySelector('[data-lq-grab]');
      if (grab) {
        let y0 = null;
        grab.addEventListener('pointerdown', (e) => {
          y0 = e.clientY;
          grab.setPointerCapture(e.pointerId);
          panel.classList.add('is-dragging');
        });
        grab.addEventListener('pointermove', (e) => {
          if (y0 === null) return;
          panel.style.setProperty('--lq-drag', `${Math.max(0, e.clientY - y0)}px`);
        });
        const end = (e) => {
          if (y0 === null) return;
          const dy = e.clientY - y0;
          y0 = null;
          panel.classList.remove('is-dragging');
          panel.style.removeProperty('--lq-drag');
          if (dy > 90) {
            const close = panel.querySelector('[data-drawer-close-btn]');
            if (close) close.click();
          }
        };
        grab.addEventListener('pointerup', end);
        grab.addEventListener('pointercancel', end);
      }
    }

    /* 10. Focus gardé après un filtrage ------------------------------------------------------ */
    // Dernière commande touchée dans un bloc rafraîchi par Flow : clé du bloc, puis nom et valeur (cases, interrupteur),
    // bornes (pilules de prix) ou libellé (liens). Quand Flow l'a retirée de la page et que le focus est tombé sur <body>,
    // on la retrouve dans le nouveau contenu (sans défilement) ; à défaut, la 1re commande visible du même bloc, puis de la
    // feuille ouverte (le piège à focus de Flow ne retient plus rien quand le focus est sur <body>).
    let lastFocus = null;
    const esc = (v) => (window.CSS && CSS.escape ? CSS.escape(v) : String(v).replace(/["\\]/g, '\\$&'));
    const shown = (el) => !!el && el.isConnected && el.getClientRects().length > 0;
    const usable = (el) => shown(el) && !el.disabled && !el.closest('[inert]');
    coll.addEventListener('focusin', (e) => {
      const el = e.target;
      const box = el.closest && el.closest('[data-filter-update]');
      lastFocus = box ? {
        el,
        key: box.getAttribute('data-filter-update'),
        name: el.getAttribute('name'),
        value: el.getAttribute('value'),
        min: el.getAttribute('data-lq-price-min'),
        max: el.getAttribute('data-lq-price-max'),
        tag: el.tagName.toLowerCase(),
        text: (el.textContent || '').replace(/\s+/g, ' ').trim(),
        inPanel: !!(panel && panel.contains(el)),
      } : null;
    });
    const findAgain = (rec) => {
      const boxes = coll.querySelectorAll(`[data-filter-update="${esc(rec.key)}"]`);
      for (const box of boxes) {
        if (!shown(box)) continue;
        let el = null;
        if (rec.name && rec.value !== null) el = box.querySelector(`[name="${esc(rec.name)}"][value="${esc(rec.value)}"]`);
        else if (rec.min !== null) el = box.querySelector(`[data-lq-price-min="${esc(rec.min)}"][data-lq-price-max="${esc(rec.max || '')}"]`);
        if (!el && rec.text) el = Array.from(box.querySelectorAll(rec.tag)).find((c) => (c.textContent || '').replace(/\s+/g, ' ').trim() === rec.text) || null;
        if (usable(el)) return { el, same: true };
        el = Array.from(box.querySelectorAll('input, button, select, a[href]')).find(usable);
        if (el) return { el, same: false };
      }
      return null;
    };
    let refocusQueued = false;
    new MutationObserver(() => {
      if (!lastFocus || refocusQueued || lastFocus.el.isConnected) return;
      refocusQueued = true;
      requestAnimationFrame(() => {
        refocusQueued = false;
        const rec = lastFocus;
        if (!rec || rec.el.isConnected) return;
        const now = d.activeElement;
        if (now && now !== body && now.isConnected) return; // la personne est déjà ailleurs
        const hit = findAgain(rec);
        if (hit) { hit.el.focus({ preventScroll: hit.same }); return; }
        if (rec.inPanel && panel && body.classList.contains(OPEN_CLASS)) {
          const next = Array.from(panel.querySelectorAll('.facets__form-inner.mobile :is(summary, select, input, button, a[href]), [data-lq-sheet-done]')).find(usable);
          if (next) next.focus();
        }
      });
    }).observe(coll, { childList: true, subtree: true });

    /* 1. Bouton flottant ------------------------------------------------------------------ */
    // Visible entre la barre d'outils (dépassée) et la fin de la grille : dès que le bas de la grille (pagination
    // comprise) entre à l'écran, il s'efface, avant la FAQ et le pied de page. Téléphone et tablette (CSS).
    const toolbar = coll.querySelector('[data-lq-toolbar]');
    const gridEnd = coll.querySelector('[data-lq-grid-end]');
    let watchEnd = () => {};
    if (fab && toolbar && 'IntersectionObserver' in window) {
      // Fin de la grille = haut de la pagination (« Afficher plus ») s'il y en a une, sinon le repère après la grille.
      let watched = null;
      // L'état est relu sur les positions réelles à chaque signal : un saut (ancre, touche Fin, scrollIntoView) peut
      // faire passer la fin de la grille de sous l'écran à au-dessus sans la croiser, donc sans notification de
      // l'observateur ; le bouton restait alors affiché sur la FAQ et le pied de page.
      const sync = () => {
        const vh = window.innerHeight || d.documentElement.clientHeight;
        const passed = toolbar.getBoundingClientRect().bottom <= 0;
        const endReached = !!watched && watched.getBoundingClientRect().top < vh;
        const on = passed && !endReached;
        coll.classList.toggle('is-fab-on', on);
        // Classe sur <html> pour masquer le « haut de page » de Flow (remplace un html:has(), coûteux à chaque mutation).
        d.documentElement.classList.toggle('lq-fab-on', on);
      };
      const io = new IntersectionObserver(sync);
      io.observe(toolbar);
      watchEnd = () => {
        const t = coll.querySelector('.lq-pagination') || gridEnd;
        if (!t || t === watched) return;
        if (watched) io.unobserve(watched);
        watched = t;
        io.observe(t);
      };
      watchEnd();
      window.addEventListener('scrollend', sync, { passive: true });
    }

    /* 4. Pilules de filtre : filtrage sur place ------------------------------------------ */
    const chipsBox = coll.querySelector('.lq-toolbar__chips');
    // Pilule active hors de vue (rangée défilante au téléphone) : ramenée au début de la rangée. L'IntersectionObserver
    // livre sa réponse après la mise en page : aucune lecture forcée pendant le chargement.
    let chipIO = null;
    const centerActive = () => {
      const nav = chipsBox && chipsBox.querySelector('.lq-chips');
      const cur = nav && nav.querySelector('[aria-current]');
      if (!nav || !cur || !('IntersectionObserver' in window)) return;
      if (chipIO) chipIO.disconnect();
      chipIO = new IntersectionObserver((entries) => {
        chipIO.disconnect();
        const en = entries[0];
        if (!en || en.intersectionRatio >= 0.99 || nav.scrollWidth <= nav.clientWidth) return;
        const pad = parseFloat(getComputedStyle(nav).paddingLeft) || 0;
        nav.scrollLeft = Math.max(0, cur.offsetLeft - pad);
      }, { root: nav, threshold: [0, 1] });
      chipIO.observe(cur);
    };
    if (chipsBox) {
      centerActive();
      new MutationObserver(centerActive).observe(chipsBox, { childList: true });
      chipsBox.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-lq-chips="filter"] a[data-lq-chip]');
        if (!chip || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
        const bus = window.eventBus;
        if (!bus || typeof bus.emit !== 'function' || !customElements.get('collection-filtering-form') || !d.querySelector('collection-filtering-form')) return;
        e.preventDefault();
        if (chip.hasAttribute('aria-current')) return;
        chip.parentElement.querySelectorAll('[aria-current]').forEach((c) => c.removeAttribute('aria-current'));
        chip.setAttribute('aria-current', 'true');
        const params = new URL(chip.href, window.location.href).searchParams.toString();
        bus.emit('collection:filter:apply', { filterParams: params });
      });
    }

    /* 5. Épuisés en fin de grille, « Afficher plus » ------------------------------------- */
    const results = coll.querySelector('#CollectionProductGrid');
    // Cartes déjà vues : WeakSet (aucune écriture dans le DOM au chargement, donc aucune invalidation de styles).
    const seen = new WeakSet();
    coll.querySelectorAll('[data-lq-grid] > .lq-cell').forEach((c) => seen.add(c));
    // Région d'annonce (lecteurs d'écran) : « 36 produits ajoutés ».
    let live = coll.querySelector('[data-lq-live]');
    if (!live) {
      live = d.createElement('p');
      live.className = 'lq-sr';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('data-lq-live', '');
      coll.appendChild(live);
    }
    let loadMoreAsked = false;
    coll.addEventListener('click', (e) => {
      if (e.target.closest('.pagination__load-more, .btn--load-more, .pagination--load-more a')) loadMoreAsked = true;
    }, true);
    let focusTimer = 0;
    const afterLoad = (fresh) => {
      const cards = fresh.filter((c) => c.querySelector('.lq-card'));
      if (!loadMoreAsked || !cards.length) return;
      clearTimeout(focusTimer);
      focusTimer = setTimeout(() => {
        loadMoreAsked = false;
        const first = cards.find((c) => !c.hasAttribute('data-soldout')) || cards[0];
        const link = first && first.querySelector('.lq-card__title a');
        if (link) link.focus({ preventScroll: true });
        const tpl = coll.getAttribute('data-lq-added') || '[count] produits ajoutés';
        live.textContent = '';
        setTimeout(() => { live.textContent = tpl.replace('[count]', String(cards.length)); }, 60);
      }, 120);
    };
    const sinkSoldOut = () => {
      const grid = coll.querySelector('[data-lq-grid]');
      if (!grid) return;
      const cells = Array.from(grid.children);
      const firstOut = cells.findIndex((c) => c.hasAttribute('data-soldout'));
      if (firstOut === -1) return;
      const misplaced = cells.slice(firstOut).some((c) => !c.hasAttribute('data-soldout') && c.classList.contains('lq-cell'));
      if (!misplaced) return;
      cells.filter((c) => c.hasAttribute('data-soldout')).forEach((c) => grid.appendChild(c));
    };
    // Cartes vraiment nouvelles (« Afficher plus », filtre) : fondu bref. Les épuisés de la page déjà vue, déplacés en
    // fin de grille, ne sont pas rejoués.
    if (results) {
      let queued = false;
      let fresh = [];
      new MutationObserver((records) => {
        records.forEach((r) => {
          if (!(r.target.hasAttribute && r.target.hasAttribute('data-lq-grid'))) return;
          r.addedNodes.forEach((n) => {
            if (n.nodeType === 1 && n.classList.contains('lq-cell') && !seen.has(n)) {
              n.classList.add('is-new');
              seen.add(n);
              fresh.push(n);
            }
          });
        });
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          sinkSoldOut();
          watchEnd();
          const batch = fresh;
          fresh = [];
          if (batch.length) afterLoad(batch);
        });
      }).observe(results, { childList: true, subtree: true });
    }

    /* 8. Recherche : « Afficher plus » sur place -------------------------------------------- */
    coll.addEventListener('click', (e) => {
      const link = e.target.closest('[data-lq-more-link]');
      if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
      const nav = link.closest('[data-lq-more-nav]');
      const grid = coll.querySelector('#CollectionProductGrid [data-lq-grid]');
      if (!nav || !grid || typeof window.fetch !== 'function' || !window.DOMParser) return;
      e.preventDefault();
      if (link.getAttribute('aria-busy') === 'true') return;
      link.setAttribute('aria-busy', 'true');
      loadMoreAsked = true;
      const url = new URL(link.href, window.location.href);
      const sid = coll.getAttribute('data-wetheme-section-id');
      if (sid) url.searchParams.set('section_id', sid);
      fetch(url.toString(), { credentials: 'same-origin', headers: { Accept: 'text/html' } })
        .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.text(); })
        .then((html) => {
          // Réponse incomplète (erreur de rendu de Shopify) : on passe par la vraie page suivante.
          if (html.indexOf('Liquid error') > -1) throw new Error('liquid');
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const cells = doc.querySelectorAll('#CollectionProductGrid [data-lq-grid] > .lq-cell');
          if (!cells.length) throw new Error('vide');
          const frag = d.createDocumentFragment();
          cells.forEach((c) => frag.appendChild(d.importNode(c, true)));
          grid.appendChild(frag);
          const next = doc.querySelector('[data-lq-more-nav]');
          if (next) nav.replaceWith(d.importNode(next, true));
          else nav.remove();
          watchEnd();
        })
        .catch(() => { window.location.href = link.href; });
    });
  });
})();
